import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
} from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { Instrument } from '@prlw/core/monitor/instrument.entity';
import { ManageOverlayService } from '../../manage-overlay/manage-overlay.service';
import { BasisOverlayService } from '../../basis-overlay/basis-overlay.service';
import { ShipmentsProvider } from '@prlw/core/shipments/shipments.provider';
import {
  MainTableColumn,
  MainTableColumnId,
  ValuesChanges,
} from '@prlw/core/monitor/types/main-table';
import { ProductsService } from '@prlw/core/products/products.service';
import { map } from 'rxjs/operators';
import { MainTableService } from './main-table.service';
import { MainTableHighlightService } from '@prlw/core/monitor/main-table-highlight.service';
import { MainTableInstrumentsService } from '@prlw/core/monitor/main-table-instruments.service';
import {
  OverflowFader,
  OverflowState,
  ScrollableElements,
} from '@prlw/libs/overflow-fader/overflow-fader.class';
import { OverflowFaderFactory } from '@prlw/libs/overflow-fader/overflow-fader-factory.service';
import { PinInstrumentService } from '@prlw/core/monitor/pin-instrument.service';
import {
  TARIFFS_GATEWAY,
  TariffsGateway,
} from '@prlw/core/tariffs/tariffs.gateway';
import { ResponsiveService } from '@prlw/libs/responsive/responsive.service';
import { MobileOverlayService } from '@prlw/libs/mobile-overlay/mobile-overlay.service';
import { BasisDetailsMobileContainer } from '../../table-content/basis-details-mobile/basis-details-mobile.container';
import {
  mapPending,
  mapResult,
} from '@prlw/infrastructure/observableWithPending-v2';
import { PagePreloaderService } from '@prlw/common/page/page-preloader.service';
import { BasisDetailsService } from '../../table-content/basis-details/basis-details.service';

@Component({
  selector: 'prlw-table',
  templateUrl: './table.container.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MainTableHighlightService],
})
export class TableContainer implements OnDestroy {
  public data$?: Observable<Instrument[] | null>;
  public columns$: Observable<MainTableColumn<MainTableColumnId>[]>;
  public valuesChanges$: Observable<ValuesChanges>;
  public hasUnselectedBases$: Observable<boolean>;
  public noBasesAvailable$: Observable<boolean>;
  public noSubProductsSelected$: Observable<boolean>;
  public gridSize$: Observable<string>;
  public overflows$: Observable<OverflowState>;
  public preloaderActive$: Observable<boolean>;
  public reqInstrumentsResult$: Observable<unknown>;
  private readonly overflowFader: OverflowFader;
  public pinnedInstrument$: Observable<Instrument | undefined>;

  constructor(
    private readonly productsService: ProductsService,
    private readonly shipmentsProvider: ShipmentsProvider,
    private readonly manageOverlayService: ManageOverlayService,
    private readonly basisOverlayService: BasisOverlayService,
    private readonly mainTableService: MainTableService,
    private readonly mainTableInstrumentsService: MainTableInstrumentsService,
    private readonly pinInstrumentService: PinInstrumentService,
    @Inject(TARIFFS_GATEWAY) private readonly tariffsGateway: TariffsGateway,
    private readonly mainTableHighlightService: MainTableHighlightService,
    private readonly overflowFaderFactory: OverflowFaderFactory,
    private readonly cdr: ChangeDetectorRef,
    private readonly responsiveService: ResponsiveService,
    private readonly mobileOverlayService: MobileOverlayService,
    public readonly pagePreloaderService: PagePreloaderService,
    private readonly basisDetailsService: BasisDetailsService,
  ) {
    this.preloaderActive$ =
      this.mainTableInstrumentsService.reqInstrumentsWithPending$.pipe(
        mapPending,
      );
    this.reqInstrumentsResult$ =
      this.mainTableInstrumentsService.reqInstrumentsWithPending$.pipe(
        mapResult,
      );

    this.data$ = combineLatest([
      mainTableService.sortedInstruments$,
      shipmentsProvider.unselectedShipmentsForCurrentProductGroup$,
      pinInstrumentService.pinnedInstrument$,
      responsiveService.xsmall$,
    ]).pipe(
      map(([instruments, unselectedShipments, pinnedInstrument, xsmall]) =>
        xsmall
          ? instruments
          : instruments
              .filter(
                (instrument) =>
                  !unselectedShipments
                    .map((unselectedShipment) => unselectedShipment.name)
                    .includes(instrument.shipment.name),
              )
              .filter(
                (instrument) =>
                  instrument.instrumentCode !==
                  pinnedInstrument?.instrumentCode,
              ),
      ),
    );
    this.noSubProductsSelected$ =
      productsService.selectedProductsOfCurrentGroup$.pipe(
        map((products) => !products.length),
      );
    this.valuesChanges$ = mainTableHighlightService.valuesChanges$;
    this.columns$ = mainTableService.visibleColumnsLocalPriceFirst$;
    this.hasUnselectedBases$ =
      shipmentsProvider.hasUnselectedShipmentsForCurrentProductGroup$;
    this.noBasesAvailable$ =
      shipmentsProvider.shipmentsByCurrentProductGroup$.pipe(
        map((shipments) => !shipments.length),
      );
    this.gridSize$ = mainTableService.gridSize$;
    this.overflowFader = overflowFaderFactory.create(cdr);
    this.overflows$ = this.overflowFader.overflows$;
    this.pinnedInstrument$ = this.pinInstrumentService.pinnedInstrument$;
  }

  isExpanded$ = this.mainTableService.isExpanded$;

  public openManageOverlay(): void {
    this.manageOverlayService.show();
  }

  public openBasisOverlay(): void {
    this.basisOverlayService.show();
  }

  public removeBasis(basisCode: string): void {
    this.shipmentsProvider.removeShipmentForCurrentProductGroup(basisCode);
  }

  public unselectProduct(name: string): void {
    this.productsService.unselectProduct(name); // FIXME: name is used
  }

  public setScrollHorizontalPosition(scrollLeft: number): void {
    this.overflowFader.setScrollHorizontalPosition(scrollLeft);
  }

  public setScrollVerticalPosition(scrollTop: number): void {
    this.overflowFader.setScrollVerticalPosition(scrollTop);
  }

  public initResizeObserver(elems: ScrollableElements): void {
    this.overflowFader.init(elems, [
      this.mainTableService.visibleColumns$,
      this.mainTableService.sortedInstruments$,
    ]);
  }

  public ngOnDestroy(): void {
    this.overflowFader.destroy();
  }

  public handleToggleActiveMobile(param: { instrumentCode: string }) {
    this.mobileOverlayService.show(
      BasisDetailsMobileContainer,
      of(param.instrumentCode),
    );
  }
}
